import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import Sidebar from '../components/admin/Sidebar';
import TopNavbar from '../components/admin/TopNavbar';
import CustomerStatsCards from '../components/admin/customers/CustomerStatsCards';
import CustomerFilterTabs from '../components/admin/customers/CustomerFilterTabs';
import CustomerSearchBar from '../components/admin/customers/CustomerSearchBar';
import CustomersTable from '../components/admin/customers/CustomersTable';
import CustomerDetailsModal from '../components/admin/customers/CustomerDetailsModal';
import './CustomersManagement.css';

const CustomersManagement = () => {
  const navigate = useNavigate();
  const [adminSession, setAdminSession] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('totalSpent');
  const [dateFilter, setDateFilter] = useState('allTime');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 289,
    newThisMonth: 45,
    active: 198
  });

  const itemsPerPage = 20;

  useEffect(() => {
    const session = localStorage.getItem('adminSession') || sessionStorage.getItem('adminSession');
    if (!session) {
      navigate('/admin');
      return;
    }
    setAdminSession(JSON.parse(session));
  }, [navigate]);

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [activeFilter, searchQuery, sortBy, dateFilter, currentPage]);

  const fetchStats = async () => {
    try {
      // Fetch total customers
      const { count: totalCount } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true });

      // Fetch new customers this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: newThisMonthCount } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString());

      // Fetch active customers (customers with orders in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: recentOrders } = await supabase
        .from('orders')
        .select('customer_id')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const activeCustomerIds = new Set(
        (recentOrders || [])
          .map(order => order.customer_id)
          .filter(id => id !== null)
      );

      setStats({
        total: totalCount || 0,
        newThisMonth: newThisMonthCount || 0,
        active: activeCustomerIds.size
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        total: 0,
        newThisMonth: 0,
        active: 0
      });
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // Fetch all customers from Supabase
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (customersError) {
        console.error('Error fetching customers:', customersError);
        setCustomers([]);
        setTotalCustomers(0);
        setLoading(false);
        return;
      }

      // Fetch all orders to calculate customer statistics
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('customer_id, total, created_at');

      if (ordersError) {
        console.error('Error fetching orders:', ordersError);
      }

      // Calculate statistics for each customer
      const ordersByCustomer = {};
      (ordersData || []).forEach(order => {
        if (order.customer_id) {
          if (!ordersByCustomer[order.customer_id]) {
            ordersByCustomer[order.customer_id] = [];
          }
          ordersByCustomer[order.customer_id].push(order);
        }
      });

      // Transform customers data
      const transformedCustomers = (customersData || []).map(customer => {
        const customerOrders = ordersByCustomer[customer.id] || [];
        const totalOrders = customerOrders.length;
        const totalSpent = customerOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
        const averageOrder = totalOrders > 0 ? totalSpent / totalOrders : 0;

        // Find last order
        const lastOrder = customerOrders.length > 0
          ? customerOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
          : null;

        const lastOrderDate = lastOrder ? new Date(lastOrder.created_at) : null;
        const daysAgo = lastOrderDate
          ? Math.floor((new Date() - lastOrderDate) / (1000 * 60 * 60 * 24))
          : null;

        // Determine badge based on total spent
        let badge = 'New';
        if (totalSpent >= 10000) {
          badge = 'Premium';
        } else if (totalSpent >= 5000) {
          badge = 'Regular';
        } else if (totalOrders > 0) {
          badge = 'Regular';
        }

        // Determine status (active if ordered in last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const hasRecentOrder = lastOrderDate && lastOrderDate >= thirtyDaysAgo;
        const status = hasRecentOrder ? 'active' : 'inactive';

        return {
          id: customer.id,
          name: customer.name,
          phone: customer.phone || 'N/A',
          email: customer.email,
          profilePhoto: null,
          badge: badge,
          totalOrders: totalOrders,
          totalSpent: totalSpent,
          averageOrder: averageOrder,
          lastOrderDate: lastOrderDate ? lastOrderDate.toISOString().split('T')[0] : null,
          lastOrderTime: lastOrderDate ? lastOrderDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null,
          daysAgo: daysAgo,
          status: status,
          created_at: customer.created_at,
          delivery_address: customer.delivery_address
        };
      });

      setCustomers(transformedCustomers);
      setTotalCustomers(transformedCustomers.length);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
      setTotalCustomers(0);
    } finally {
      setLoading(false);
    }
  };


  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setIsDetailsModalOpen(true);
  };


  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filter customers based on active filter
  const filteredCustomers = customers.filter(customer => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Top Spenders') return customer.totalSpent > 10000;
    if (activeFilter === 'Regular') return customer.badge === 'Regular';
    if (activeFilter === 'New') return customer.badge === 'New';
    if (activeFilter === 'Inactive') return customer.status === 'inactive';
    return true;
  });

  // Apply search filter
  const searchedCustomers = filteredCustomers.filter(customer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      customer.name.toLowerCase().includes(query) ||
      customer.phone.includes(query) ||
      customer.email.toLowerCase().includes(query)
    );
  });

  // Sort customers
  const sortedCustomers = [...searchedCustomers].sort((a, b) => {
    if (sortBy === 'totalSpent') return b.totalSpent - a.totalSpent;
    if (sortBy === 'totalOrders') return b.totalOrders - a.totalOrders;
    if (sortBy === 'lastOrder') return new Date(b.lastOrderDate) - new Date(a.lastOrderDate);
    return a.name.localeCompare(b.name);
  });

  // Paginate
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCustomers = sortedCustomers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  if (!adminSession) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="customers-management">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="customers-main">
        <TopNavbar 
          onMenuClick={() => setSidebarOpen(true)}
          adminName={adminSession.username || adminSession.email}
        />
        
        <main className="customers-content">
          <div className="page-header">
            <div>
              <h1 className="page-title">Customers</h1>
              <nav className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">/</span>
                <span>Customers</span>
              </nav>
            </div>
          </div>

          <CustomerStatsCards stats={stats} />

          <CustomerFilterTabs
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />

          <CustomerSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />

          <CustomersTable
            customers={paginatedCustomers}
            loading={loading}
            onViewDetails={handleViewDetails}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCustomers={sortedCustomers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />

          {isDetailsModalOpen && selectedCustomer && (
            <CustomerDetailsModal
              customer={selectedCustomer}
              onClose={() => {
                setIsDetailsModalOpen(false);
                setSelectedCustomer(null);
              }}
            />
          )}

        </main>
      </div>
    </div>
  );
};

export default CustomersManagement;

